import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import Body from 'ui/Typography/Body';
import Title, { HeadingType } from 'ui/Typography/Title';
import '@ingka/svg-icon/dist/style.css';
import '@ingka/button/dist/style.css';
import '@ingka/modal/dist/style.css';
import Modal, { ModalFooter, ModalHeader, ModalBody, Sheets } from '@ingka/modal';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums/lib';
import { useIntl } from 'react-intl';
import {
  hideClothHangersSheet,
  isModalVisible,
  setDismissed,
  wasDismissed
} from './ClothHangersFeature';
import { useAppDispatch } from 'ducks/hooks';
import { getMarketProducts, getSelectedGroupId } from 'ducks/products';
import { ProductType } from '@inter-ikea-storageone/range';
import styled from 'styled-components';
import HangersImage from './hangers.jpg';
import ProductImage from 'components/ProductImage/ProductImage';
import { getMPPImage, getProductDescription } from 'products';
import PriceModule from 'ui/PriceModule/PriceModule';
import useFetchShoppingProducts from 'hooks/useFetchShoppingProducts';
import Divider from 'ui/Divider/Divider';
import { PriceModuleLayoutTypeEnum } from '@inter-ikea-kompis/component-price-module';
import Button from '@ingka/button';
import PlusIcon from '@ingka/ssr-icon/paths/plus';
import QuantityStepper from '@ingka/quantity-stepper';
import { useCore } from 'contexts/core';
import { invalidateVpcCode } from 'ducks/vpc';
import { allRailProductTypes } from 'rangeConfig/shared/utils/clothHangers';
import { resetDesignId } from 'ducks/designId';
import { useBehaviorTracker } from 'contexts/behaviors';

const Image = styled.img`
  width: 100%;

  @media (min-width: 600px) {
    padding: 2rem;
  }
`;

const ProductList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.md};
`;

const ProductRow = styled.div`
  display: grid;
  grid-template-columns: 80px 1fr auto;
  align-items: center;
  gap: ${({ theme }) => theme.space.md};
`;

const ClothHangersSheet = () => {
  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();
  const products = useSelector(getMarketProducts);
  const modalVisible = useSelector(isModalVisible);
  const modalDismissed = useSelector(wasDismissed);
  const groupId = useSelector(getSelectedGroupId);
  const visible = modalVisible && !modalDismissed;
  const coreApi = useCore();
  const behaviorTracker = useBehaviorTracker();

  const clothHangerProducts = useMemo(
    () => products.filter(p => p.type === ProductType.CLOTH_HANGERS),
    [products]
  );

  const { shoppingProducts } = useFetchShoppingProducts(clothHangerProducts);

  const [addedProducts, setAddedProducts] = useState<Record<string, number>>({});

  const anyHangers = Object.values(addedProducts).reduce((acc, curr) => acc + curr, 0) > 0;

  const setAmount = (id: string, amount: number) =>
    setAddedProducts(ap => ({ ...ap, [id]: amount }));

  const onDismiss = () => {
    setAddedProducts({});
    dispatch(hideClothHangersSheet());
    dispatch(setDismissed(true));
  };

  const onFinalize = () =>
    behaviorTracker
      .withContext({
        menuType: 'sheet',
        groupId,
        widgetId: 'bumerang-hangers-sheet',
        meta: {
          actionTaken: anyHangers,
          color: anyHangers
            ? (Object.keys(addedProducts)
                .map(coreProductId =>
                  clothHangerProducts.find(p => p.coreProductId === coreProductId)
                )
                .flatMap(product => product?.meta?.matchesColors)
                .filter(Boolean) as string[])
            : undefined
        }
      })
      .custom(() => {
        if (anyHangers) {
          Object.entries(addedProducts).forEach(([coreProductId, amount]) => {
            for (let i = 0; i < amount; i++) {
              const entity = coreApi.entities.create(coreProductId);
              entity && coreApi.entities.placementService.forcePlaceEntity(entity);
            }
          });

          dispatch(invalidateVpcCode());
          dispatch(resetDesignId());
        }

        onDismiss();
      });

  useEffect(() => {
    const onHistoryEvent = () => {
      const railsInScene = coreApi.entities.getByProductTypes(allRailProductTypes);
      const clothHangersInScene = coreApi.entities.getByProductType(ProductType.CLOTH_HANGERS);

      if (!railsInScene.length) {
        coreApi.entities.deleteMultiple(clothHangersInScene);
        dispatch(setDismissed(false));
      }
    };

    return coreApi.subscribe('history', onHistoryEvent);
  }, [coreApi, dispatch]);

  const sortedHangers = clothHangerProducts.sort((a, b) => {
    const designOrder = ['natural', 'white', 'black'];
    const aDesignIdx = designOrder.indexOf(a.meta?.matchesColors?.[0] as string);
    const bDesignIdx = designOrder.indexOf(b.meta?.matchesColors?.[0] as string);

    return aDesignIdx < bDesignIdx ? -1 : 1;
  });

  return (
    <Modal data-testid="cloth-hangers-sheet" visible={visible} handleCloseBtn={onFinalize}>
      <Sheets
        title={formatMessage({ id: 'storageone.clothHangersSheetTitle' })}
        header={
          <ModalHeader
            data-testid="modal-header"
            ariaCloseTxt={formatMessage({ id: 'storageone.closeButton' })}
          />
        }
        footer={
          <ModalFooter>
            <Button
              text={formatMessage({
                id: anyHangers
                  ? 'storageone.clothHangersSheetButtonAdd'
                  : 'storageone.clothHangersSheetButtonContinue'
              })}
              onClick={onFinalize}
              type={anyHangers ? 'primary' : undefined}
              data-testid="cloth-hangers-sheet-finalize-btn"
              fluid
            />
          </ModalFooter>
        }
      >
        <ModalBody style={{ paddingTop: 0 }}>
          <Title
            mb="xl"
            type={ThemeFontStyleTypeEnum.headingMedium}
            heading={HeadingType.HeadingTwo}
          >
            {formatMessage({ id: 'storageone.clothHangersSheetTitle' })}
          </Title>
          <Body type={ThemeFontStyleTypeEnum.bodyMedium}>
            {formatMessage({ id: 'storageone.clothHangersSheetDescription' })}
          </Body>
          <Image
            src={HangersImage}
            alt={formatMessage({ id: 'storageone.clothHangersSheetTitle' })}
          />
          <ProductList>
            {sortedHangers.map(product => {
              const image = getMPPImage(product);
              const shoppingProduct = shoppingProducts.find(
                p => p.product.content?.ruItemNo === product.ruItemNo
              );

              if (!shoppingProduct || !image) return;

              const description = getProductDescription(product, {
                typeName: true,
                designText: true
              });

              const id = product.coreProductId;
              const amount = addedProducts[id];

              return (
                <>
                  <ProductRow>
                    <ProductImage alt={product.name} image={image} sizes="10rem" />
                    <PriceModule
                      shoppingProducts={[shoppingProduct]}
                      productName={product.name}
                      showPriceUnit={true}
                      layout={PriceModuleLayoutTypeEnum.rowPrice}
                    >
                      {description}
                    </PriceModule>
                    {amount ? (
                      <QuantityStepper
                        small
                        value={amount}
                        minVal={0}
                        maxVal={100}
                        ariaDescribedBy={formatMessage({
                          id: 'storageone.clothHangersSheetQuantityStepperDescription'
                        })}
                        ariaDescribedById="bumerangHelper" // Id of span element containing the translation string above
                        onIncrease={() => setAmount(id, amount + 1)}
                        onDecrease={() => setAmount(id, amount - 1)}
                      />
                    ) : (
                      <Button
                        iconOnly
                        type="primary"
                        size="small"
                        ssrIcon={PlusIcon}
                        onClick={() => setAmount(id, 1)}
                      />
                    )}
                  </ProductRow>
                  <Divider />
                </>
              );
            })}
          </ProductList>
          <br />
          <Body type={ThemeFontStyleTypeEnum.bodyMedium}>
            {formatMessage({ id: 'storageone.clothHangersSheetInfoMessage' })}
          </Body>
        </ModalBody>
      </Sheets>
    </Modal>
  );
};

export default ClothHangersSheet;
